import { v4 as uuid } from 'uuid';
import type {
  StudioCatalog,
  StudioColumn,
  StudioDeck,
  StudioDefinition,
  StudioField,
  StudioScenarioBlock,
  StudioVarianceBlock
} from 'src/components/star-grid/reportStudio.types';
import { canonicalTemplateJson } from 'src/components/star-grid/utils/templateDraft';

export const studioKey = canonicalTemplateJson;
export const studioDeck = (definition: StudioDefinition): StudioDeck =>
  definition.decks[definition.active_deck] ?? { rows: [], filters: [], columns: [], line_sets: [] };
export const replaceStudioDeck = (definition: StudioDefinition, deck: StudioDeck): StudioDefinition => ({
  ...definition,
  decks: definition.active_deck ? { ...definition.decks, [definition.active_deck]: deck } : definition.decks
});
export const studioField = (bindingId: string): StudioField => ({ id: uuid(), binding_id: bindingId, selection: { kind: 'all', members: [] } });
export const studioScenario = (catalog: StudioCatalog, definition: StudioDefinition, scenarioId?: string): StudioScenarioBlock => {
  const dataset = catalog.datasets.find((item) => item.id === definition.dataset_id)!;
  const scenario = catalog.scenarios.find((item) => item.id === scenarioId) ?? catalog.scenarios[0];
  return {
    id: uuid(),
    kind: 'scenario',
    label: scenario?.label ?? '',
    scenario_id: scenario?.id ?? '',
    version_id: null,
    period_ids: [],
    metric_id: dataset.metrics[0]?.id ?? '',
    currency_ids: [],
    fx_policy_id: null
  };
};
export const studioVariance = (columns: readonly StudioColumn[], baseId?: string): StudioVarianceBlock => {
  const scenarios = columns.filter((column): column is StudioScenarioBlock => column.kind === 'scenario');
  const base = scenarios.find((column) => column.id === baseId) ?? scenarios[0];
  const compare = scenarios.find((column) => column.id !== base?.id);
  return {
    id: uuid(),
    kind: 'variance',
    label: '',
    base_block_id: base?.id ?? '',
    compare_block_id: compare?.id ?? '',
    period_pairs: (base?.period_ids ?? []).map((period) => ({ base: period, compare: period })),
    direction: 'base_minus_compare',
    fx_policy_id: null,
    show_percent: true
  };
};
export const moveStudioItem = <T>(items: readonly T[], index: number, offset: number): readonly T[] => {
  const next = index + offset;
  if (next < 0 || next >= items.length) return items;
  const copy = [...items];
  [copy[index], copy[next]] = [copy[next], copy[index]];
  return copy;
};
export const removeStudioColumn = (deck: StudioDeck, id: string): StudioDeck => ({
  ...deck,
  columns: deck.columns.filter(
    (column) => column.id !== id && !(column.kind === 'variance' && (column.base_block_id === id || column.compare_block_id === id))
  )
});
/**
 * The deck without one row field, and without that field's lines in the shared rows set.
 *
 * Every row dimension appends its members to ONE line set, so dropping only the field left its members
 * listed in the set and still rendered in the preview. A dimension placed twice keeps its lines until the
 * last copy goes; a set no row points at any more is dropped with it. `sameDimension` groups the bindings
 * of one dimension, so a line on a sibling hierarchy does not stay behind as an orphan.
 */
export const removeStudioRowField = (
  deck: StudioDeck,
  id: string,
  sameDimension: (left: string, right: string) => boolean = (left, right) => left === right
): StudioDeck => {
  const removed = deck.rows.find((row) => row.id === id);
  const rows = deck.rows.filter((row) => row.id !== id);
  if (!removed) return deck;
  const stillPlaced = rows.some((row) => sameDimension(row.binding_id, removed.binding_id));
  const line_sets = deck.line_sets
    .map((set) =>
      stillPlaced || removed.selection.reference_id !== set.id
        ? set
        : { ...set, entries: set.entries.filter((entry) => !sameDimension(entry.binding_id ?? set.binding_id, removed.binding_id)) }
    )
    .filter((set) => set.id !== removed.selection.reference_id || rows.some((row) => row.selection.reference_id === set.id));
  return { ...deck, rows, line_sets };
};
export const includeStudioDecks = (definition: StudioDefinition, deckTypes: readonly string[]): StudioDefinition => ({
  ...definition,
  deck_types: [...deckTypes],
  decks: {
    ...definition.decks,
    ...Object.fromEntries(
      deckTypes.filter((type) => !definition.decks[type]).map((type) => [type, { rows: [], filters: [], columns: [], line_sets: [] }])
    )
  },
  active_deck: deckTypes.includes(definition.active_deck) ? definition.active_deck : deckTypes[0] ?? ''
});

/** The preview grid's title: the name and the sample tag, each only when present, so an unnamed report has no stray separator. */
export const studioPreviewTitle = (name: string, sampleLabel?: string): string => [name.trim(), sampleLabel ?? ''].filter(Boolean).join(' · ');
